import { FC } from "react";
import {
  Box,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  SxProps,
  Typography,
} from "@mui/material";
import CircleRoundedIcon from "@mui/icons-material/CircleRounded";
import { BurnNonSensitiveUi } from "../BurnNonSensitiveUi";
import { DurationAs } from "shared-ts-ui-time";
import { isDense, SizeClass } from "shared-ts-ui-responsive";
import { useFireflySharedTsDomainTranslation } from "../../i18n";
import { BurnStartQualityAdornment } from "./BurnStartQualityAdornment";
import { BurnSootIndexAdornment } from "./BurnSootIndexAdornment";
import { BurnMaxTemperatureAdornment } from "./BurnMaxTemperatureAdornment";

export interface BurnListItemProps {
  burn: BurnNonSensitiveUi;
  selected?: boolean;
  hovered?: boolean;
  hideDuration?: boolean;
  hideMarker?: boolean;
  layout?: SizeClass;
  dense?: boolean;
  adornment?: "startQuality" | "sootIndex" | "temperatureMax";
  onHover?: (hovered: boolean) => void;
  onSelected?: (selected: boolean) => void;
  direction?: "row" | "column";
  sx?: SxProps;
}

export const BurnListItem: FC<BurnListItemProps> = (props) => {
  const { t } = useFireflySharedTsDomainTranslation();
  const layout = props.layout ?? SizeClass.Compact;
  const dense = props.dense ?? isDense(layout);
  const hideDuration = props.hideDuration ?? layout === SizeClass.Compact;
  const hideExtraInfoIcon = layout === SizeClass.Compact;
  const duration = props.burn.duration;
  const hideMarker = props.hideMarker ?? false;
  const adornment = props.adornment ?? "startQuality";

  const handleClick = () => {
    props.onSelected?.(!(props.selected ?? false));
  };

  const handleMouseEnter = () => {
    props.onHover?.(true);
  };

  const handleMouseLeave = () => {
    props.onHover?.(false);
  };

  return (
    <ListItemButton
      dense={dense}
      key={props.burn.id}
      selected={props.selected}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        borderRadius: "18px",
        margin: "3px",
        pl: "10px",
        pr: "10px",
        minWidth: props.direction === "row" ? "fit-content" : undefined,
      }}
    >
      <Stack
        direction={"row"}
        useFlexGap={true}
        gap={1}
        sx={{ flex: 1, alignItems: "center" }}
      >
        {!hideMarker && (
          <ListItemIcon
            sx={{
              color: props.burn.color,
              alignItems: "center",
              minWidth: "unset",
              marginRight: 1,
            }}
          >
            <CircleRoundedIcon />
          </ListItemIcon>
        )}
        <ListItemText sx={{ minWidth: "30px" }}>
          #{props.burn.orderOfOccurence}
        </ListItemText>
        {!hideDuration && (
          <Typography title={t("The burns duration")}>
            <DurationAs
              duration={duration}
              unit={"hour"}
              decimalPrecision={0}
            />
          </Typography>
        )}
        {!hideExtraInfoIcon && (
          <Box
            sx={{
              ml: 2,
              minWidth: "20px",
              display: "flex",
              alignItems: "center",
            }}
          >
            {adornment === "startQuality" && (
              <BurnStartQualityAdornment
                startQuality={props.burn.analytics.startQuality}
                chimneyFire={props.burn.metrics.chimneyFire}
              />
            )}
            {adornment === "sootIndex" && (
              <BurnSootIndexAdornment value={props.burn.metrics.sootIndex} />
            )}
            {adornment === "temperatureMax" && (
              <BurnMaxTemperatureAdornment
                value={props.burn.metrics.temperatureMax}
              />
            )}
          </Box>
        )}
      </Stack>
    </ListItemButton>
  );
};
